import React, { useState, useEffect } from 'react';
import Header from '../components/Header.jsx';
import { adminAPI } from '../services/api';

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'rudraksh',
    price: '',
    stock_quantity: '',
    is_active: true,
    images: [''] // Array of image URLs
  });

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      const response = await adminAPI.getProducts();
      setProducts(response.data.products || []);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const productData = {
        ...formData,
        // Clean images: trim whitespace and remove any line breaks from Base64
        images: formData.images
          .map(url => url.replace(/\s+/g, '').trim()) // Remove all whitespace including newlines
          .filter(url => url !== '')
      };

      if (editingProduct) {
        await adminAPI.updateProduct(editingProduct.product_id, productData);
        alert('Product updated successfully');
      } else {
        await adminAPI.createProduct(productData);
        alert('Product created successfully');
      }

      resetForm();
      loadProducts();
    } catch (error) {
      alert('Error saving product');
    }
  };

  const deleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;

    try {
      await adminAPI.deleteProduct(productId);
      loadProducts();
      alert('Product deleted successfully');
    } catch (error) {
      alert('Error deleting product');
    }
  };

  const startEdit = (product) => {
    setEditingProduct(product);
    
    // Handle both old format (images string) and new format (imageUrls array)
    let imageArray = [''];
    if (product.imageUrls && Array.isArray(product.imageUrls)) {
      imageArray = product.imageUrls.length > 0 ? product.imageUrls : [''];
    } else if (product.images && typeof product.images === 'string') {
      imageArray = product.images.split(',').filter(img => img.trim());
      if (imageArray.length === 0) imageArray = [''];
    }
    
    setFormData({
      name: product.name,
      description: product.description,
      category: product.category,
      price: product.price,
      stock_quantity: product.stock_quantity,
      is_active: product.is_active,
      images: imageArray
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: 'rudraksh',
      price: '',
      stock_quantity: '',
      is_active: true,
      images: ['']
    });
    setEditingProduct(null);
    setShowForm(false);
  };

  const handleImageChange = (index, value) => {
    const newImages = [...formData.images];
    newImages[index] = value;
    setFormData({ ...formData, images: newImages });
  };

  const handleFileUpload = (index, event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size should be less than 5MB');
      return;
    }

    // Convert to Base64
    const reader = new FileReader();
    reader.onloadend = () => {
      const newImages = [...formData.images];
      newImages[index] = reader.result;
      setFormData({ ...formData, images: newImages });
    };
    reader.readAsDataURL(file);
  };

  const addImageField = () => {
    setFormData({ ...formData, images: [...formData.images, ''] });
  };

  const removeImageField = (index) => {
    const newImages = formData.images.filter((_, i) => i !== index);
    setFormData({ ...formData, images: newImages.length ? newImages : [''] });
  };

  return (
    <div>
      <Header />
      <main>
        <section className="section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h1 style={{ color: 'var(--maroon)', fontFamily: "'Playfair Display',serif", margin: '0' }}>
              Product Management
            </h1>
            <button
              onClick={() => setShowForm(!showForm)}
              style={{
                padding: '10px 20px',
                borderRadius: '8px',
                border: 'none',
                background: 'var(--maroon)',
                color: '#fff',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              {showForm ? 'Cancel' : '+ Add New Product'}
            </button>
          </div>

          {/* Form */}
          {showForm && (
            <div className="card" style={{ marginBottom: '20px', padding: '20px' }}>
              <h2 style={{ marginTop: '0', color: 'var(--maroon)' }}>
                {editingProduct ? 'Edit Product' : 'Add New Product'}
              </h2>
              <form onSubmit={handleSubmit}>
                <div style={{ display: 'grid', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Product Name</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      style={{ width: '100%' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Description</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      required
                      rows={4}
                      style={{ width: '100%' }}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Category</label>
                      <select
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        required
                        style={{ width: '100%' }}
                      >
                        <option value="rudraksh">Rudraksh</option>
                        <option value="gemstone">Gemstone</option>
                        <option value="yantra">Yantra</option>
                        <option value="mala">Mala</option>
                        <option value="idol">Idol</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Price (₹)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.price}
                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                        required
                        style={{ width: '100%' }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Stock Quantity</label>
                      <input
                        type="number"
                        value={formData.stock_quantity}
                        onChange={(e) => setFormData({ ...formData, stock_quantity: e.target.value })}
                        required
                        style={{ width: '100%' }}
                      />
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.is_active}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      />
                      <span style={{ fontWeight: '600' }}>Active (visible to customers)</span>
                    </label>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Product Images</label>
                    <p style={{ fontSize: '14px', color: '#666', marginBottom: '12px' }}>
                      You can upload images, paste Base64 data, or enter image URLs
                    </p>
                    {formData.images.map((img, index) => (
                      <div key={index} style={{ marginBottom: '16px', padding: '12px', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                          <textarea
                            placeholder="Enter image URL, paste Base64 data, or upload below"
                            value={img}
                            onChange={(e) => handleImageChange(index, e.target.value)}
                            style={{ 
                              flex: 1, 
                              minHeight: '40px',
                              maxHeight: '120px',
                              resize: 'vertical',
                              fontFamily: 'monospace',
                              fontSize: '12px'
                            }}
                          />
                          {formData.images.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeImageField(index)}
                              style={{
                                padding: '8px 12px',
                                borderRadius: '6px',
                                border: 'none',
                                background: '#dc2626',
                                color: '#fff',
                                cursor: 'pointer'
                              }}
                            >
                              Remove
                            </button>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <label style={{
                            padding: '8px 16px',
                            borderRadius: '6px',
                            border: '1px solid var(--maroon)',
                            background: '#fff',
                            color: 'var(--maroon)',
                            cursor: 'pointer',
                            fontSize: '14px',
                            display: 'inline-block'
                          }}>
                            📁 Upload Image
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleFileUpload(index, e)}
                              style={{ display: 'none' }}
                            />
                          </label>
                          {img && img.trim() && (
                            img.startsWith('http://') || 
                            img.startsWith('https://') || 
                            (img.startsWith('data:image') && img.length > 50)
                          ) && (
                            <img
                              src={img}
                              alt={`Preview ${index + 1}`}
                              style={{
                                width: '80px',
                                height: '80px',
                                objectFit: 'cover',
                                borderRadius: '6px',
                                border: '1px solid #e5e7eb'
                              }}
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          )}
                        </div>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addImageField}
                      style={{
                        padding: '8px 12px',
                        borderRadius: '6px',
                        border: '1px solid var(--maroon)',
                        background: '#fff',
                        color: 'var(--maroon)',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      + Add Another Image
                    </button>
                  </div>

                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button
                      type="button"
                      onClick={resetForm}
                      style={{
                        padding: '10px 20px',
                        borderRadius: '6px',
                        border: '1px solid #ccc',
                        background: '#fff',
                        cursor: 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      style={{
                        padding: '10px 20px',
                        borderRadius: '6px',
                        border: 'none',
                        background: 'var(--maroon)',
                        color: '#fff',
                        cursor: 'pointer',
                        fontWeight: '600'
                      }}
                    >
                      {editingProduct ? 'Update Product' : 'Create Product'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          )}

          {/* Products List */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '50px', color: 'var(--muted)' }}>
              Loading products...
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '16px' }}>
              {products.map((product) => {
                // Handle both old format (images string) and new format (imageUrls array)
                let imageArray = [];
                if (product.imageUrls && Array.isArray(product.imageUrls)) {
                  imageArray = product.imageUrls;
                } else if (product.images && typeof product.images === 'string') {
                  imageArray = product.images.split(',').map(img => img.trim());
                }
                
                const firstImage = imageArray[0] || '';
                // Validate image URL - must be http/https or valid Base64 (longer than just prefix)
                const isValidImage = firstImage && (
                  firstImage.startsWith('http://') || 
                  firstImage.startsWith('https://') || 
                  (firstImage.startsWith('data:image') && firstImage.length > 50)
                );
                return (
                <div key={product.product_id} className="card" style={{ padding: '20px' }}>
                  <div style={{ display: 'flex', gap: '20px' }}>
                    {isValidImage && (
                      <img
                        src={firstImage}
                        alt={product.name}
                        style={{ width: '150px', height: '150px', objectFit: 'cover', borderRadius: '8px' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                        <div>
                          <h3 style={{ margin: '0 0 8px 0', color: 'var(--maroon)' }}>{product.name}</h3>
                          <p style={{ margin: '0 0 8px 0', color: 'var(--muted)', fontSize: '14px' }}>
                            Category: {product.category.charAt(0).toUpperCase() + product.category.slice(1)}
                          </p>
                        </div>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '6px',
                          background: product.is_active ? 'rgba(5,150,105,0.1)' : 'rgba(220,38,38,0.1)',
                          color: product.is_active ? '#059669' : '#dc2626',
                          fontSize: '12px',
                          fontWeight: '600'
                        }}>
                          {product.is_active ? 'ACTIVE' : 'INACTIVE'}
                        </span>
                      </div>
                      <p style={{ margin: '8px 0', lineHeight: '1.6' }}>{product.description}</p>
                      <div style={{ display: 'flex', gap: '16px', margin: '12px 0' }}>
                        <p style={{ margin: '0', fontWeight: '700', color: 'var(--maroon)' }}>
                          ₹{parseFloat(product.price).toFixed(2)}
                        </p>
                        <p style={{ margin: '0', color: 'var(--muted)' }}>
                          Stock: {product.stock_quantity}
                        </p>
                      </div>
                      <div style={{ display: 'flex', gap: '10px', marginTop: '12px' }}>
                        <button
                          onClick={() => startEdit(product)}
                          style={{
                            padding: '8px 16px',
                            borderRadius: '6px',
                            border: 'none',
                            background: '#3b82f6',
                            color: '#fff',
                            cursor: 'pointer',
                            fontWeight: '600'
                          }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => deleteProduct(product.product_id)}
                          style={{
                            padding: '8px 16px',
                            borderRadius: '6px',
                            border: 'none',
                            background: '#dc2626',
                            color: '#fff',
                            cursor: 'pointer',
                            fontWeight: '600'
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
              })}
            </div>
          )}

          {products.length === 0 && !loading && (
            <div style={{ textAlign: 'center', padding: '50px', color: 'var(--muted)' }}>
              No products found. Click "Add New Product" to get started.
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default AdminProducts;
